import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { DailyRouteSubject, SafetyEvent } from '@apis/reports';
import { useTradeLabel } from '@hooks/useTradeLabel';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ, formatTime } from '@utils/dateUtils';
import { CardIcon } from '@components/Pages/AnalyticsPage/CardIcon';
import { PanelCard } from '../../shared/PanelCard/PanelCard';
import styles from './IncidentSummaryCard.module.css';

interface IIncidentSummaryCardProps {
  subject: DailyRouteSubject;
  /** Safety events for the subject across the whole `at ± 30 min` window. */
  safetyEvents: SafetyEvent[];
  /**
   * Maps an `areaId` to the corresponding `areaNick` from the route's
   * stops (the safety event payload only carries IDs). When the lookup
   * misses, the chip falls back to the raw ID.
   */
  resolveAreaNick: (areaId: string | null) => string | null;
  /** Same as `resolveAreaNick` but for floors. */
  resolveFloorNick: (floorId: string | null) => string | null;
  /**
   * Returns the rail's location number (`stopIndex`) for the safety
   * event, or `null` when no stop covers the event. The card uses this
   * to render the red badge — keeping the rail and the chip badges in
   * sync ("Location 39" === chip "39"). The page owns the lookup so
   * the card stays decoupled from the stops list.
   */
  resolveStopIndex: (event: SafetyEvent) => number | null;
  /**
   * Optional click handler. When provided, each safety chip becomes a
   * button that asks the page to select the stop the event belongs to.
   * The page owns the actual stop lookup so it can pick the most
   * relevant stop (e.g. by timestamp containment) without this card
   * needing the full stops list.
   */
  onSelectEvent?: (event: SafetyEvent) => void;
}

/**
 * Top hero card of the Incident Report. Mirrors trusstor's
 * `InvestigationReportTagInfoCard` / `InvestigationReportEquipmentInfoCard`:
 *
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │  <subject name>                                                  │
 *   │  🏷 <id chip>  |  <trade name>                                   │
 *   │                                                                  │
 *   │  [⚠ violation: HH:MM area floor]  [⚠ violation: ...]  ...        │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * Safety chips render in a flex-wrap grid so an arbitrary number of events
 * stack into rows without horizontal scrolling.
 */
export function IncidentSummaryCard({
  subject,
  safetyEvents,
  resolveAreaNick,
  resolveFloorNick,
  resolveStopIndex,
  onSelectEvent,
}: IIncidentSummaryCardProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ja' ? 'ja' : 'en';
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const tradeOf = useTradeLabel();

  const id = subject.kind === 'tag' ? subject.tagId : subject.equipmentId;
  const tail =
    subject.kind === 'tag'
      ? tradeOf(subject.professionTradeId) || null
      : subject.type;
  const showName = subject.name && subject.name !== id;

  return (
    <PanelCard aria-label={subject.name}>
      <div className={styles.identity}>
        {showName && <div className={styles.name}>{subject.name}</div>}
        <div className={styles.meta}>
          <CardIcon name="tag" size={16} />
          <span className={styles.idChip}>{id}</span>
          {tail !== null && (
            <>
              <span className={styles.divider} aria-hidden="true">|</span>
              <span className={styles.tail}>{tail}</span>
            </>
          )}
        </div>
      </div>

      {safetyEvents.length > 0 && (
        <div className={styles.chipGrid}>
          {safetyEvents.map((safetyEvent) => {
            const label = t(`safety.violations.${safetyEvent.safetyViolationType}`, {
              defaultValue: safetyEvent.safetyViolationType,
            });
            const time = formatTime(safetyEvent.timestamp, lang, tz);
            const area = resolveAreaNick(safetyEvent.areaId);
            const floor = resolveFloorNick(safetyEvent.floorId);
            // location tail is "<area> <floor>" when both resolve, just one
            // of them when only one resolves, and empty otherwise. Joined
            // with a thin space rather than a separator so the chip stays
            // compact when most of the metadata is missing.
            const locationTail = [area, floor].filter(Boolean).join(' ');
            const stopIndex = resolveStopIndex(safetyEvent);
            const inner = (
              <>
                {stopIndex !== null ? (
                  <span className={styles.floorBadge} aria-hidden="true">
                    {stopIndex}
                  </span>
                ) : (
                  <span className={styles.dot} aria-hidden="true" />
                )}
                <span className={styles.chipLabel}>{label}:</span>
                <span className={styles.chipMeta}>
                  {time}
                  {locationTail && ` ${locationTail}`}
                </span>
              </>
            );
            const chipClass = `${styles.chip} ${safetyEvent.isCritical ? styles.chipCritical : ''}`;
            // When the page wires `onSelectEvent`, the chip becomes a real
            // button so it's keyboard-reachable and announces a click action
            // to assistive tech. Otherwise it stays a passive <span>.
            if (onSelectEvent !== undefined) {
              return (
                <button
                  key={safetyEvent.safetyEventId}
                  type="button"
                  className={`${chipClass} ${styles.chipClickable}`}
                  onClick={() => onSelectEvent(safetyEvent)}
                  aria-label={`${label} ${time} ${locationTail}`.trim()}
                >
                  {inner}
                </button>
              );
            }
            return (
              <span key={safetyEvent.safetyEventId} className={chipClass}>
                {inner}
              </span>
            );
          })}
        </div>
      )}
    </PanelCard>
  );
}
